import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, LoadScript, DirectionsRenderer, Marker } from '@react-google-maps/api';
import { supabase } from './supabaseClient';
import './App.css';

// Função para obter coordenadas do cliente com base no código
const getClientCoordinates = (clientCode) => {
  if (!clientCode) return null;
  
  switch (clientCode.toLowerCase()) {
    case 'stb_itap':
      return { lat: -23.586211332510544, lng: -48.041478651378135 };
    // Adicione mais casos de clientes aqui conforme necessário
    default:
      console.warn(`Código de cliente não reconhecido: ${clientCode}`);
      return null;
  }
};

// Enhanced map container style for mobile
const mapContainerStyle = {
  width: '100%',
  height: '100%',
  touchAction: 'none',
  WebkitOverflowScrolling: 'touch',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0
};

const center = {
  lat: -23.586211645974206,
  lng: -48.04148147373932
};

function App() {
  const [driverId, setDriverId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [driverLocation, setDriverLocation] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [startLocation, setStartLocation] = useState(null); // Ponto de partida da URL
  const [directions, setDirections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userLocationPermission, setUserLocationPermission] = useState('pending');
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  // Referência para o objeto google depois de carregado
  const googleRef = useRef(null);

  // Get driver ID and client/location from URL query parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('id');
    const clientCode = urlParams.get('c');
    const legacyStartParam = urlParams.get('start');

    if (id) {
      setDriverId(id);
    } else {
      setError('ID do motorista não encontrado na URL');
      setLoading(false);
    }

    // Primeiro tenta usar o novo parâmetro de cliente (c=)
    if (clientCode) {
      console.log(`🔍 Cliente detectado na URL: ${clientCode}`);
      const clientCoords = getClientCoordinates(clientCode);
      if (clientCoords) {
        console.log(`📍 Coordenadas do cliente ${clientCode}:`, clientCoords);
        setStartLocation(clientCoords);
      } else {
        console.error(`❌ Não foi possível encontrar coordenadas para o cliente: ${clientCode}`);
      }
    } 
    // Fallback para o parâmetro start legado (para compatibilidade)
    else if (legacyStartParam) {
      console.warn('⚠️ Usando parâmetro start legado. Considere migrar para o novo formato c=cliente_code');
      try {
        let startCoords;

        if (legacyStartParam.startsWith('{')) {
          // Tentar como JSON
          startCoords = JSON.parse(legacyStartParam);
        } else {
          // Tentar como string lat,lng
          const [lat, lng] = legacyStartParam.split(',').map(parseFloat);
          startCoords = { lat, lng };
        }

        if (startCoords && !isNaN(startCoords.lat) && !isNaN(startCoords.lng)) {
          console.log('📍 Ponto de partida legado detectado:', startCoords);
          setStartLocation(startCoords);
        }
      } catch (e) {
        console.error('❌ Erro ao interpretar parâmetro start legado:', e);
      }
    }
  }, []);

  // Get user's location with better mobile support
  const getUserLocation = useCallback(() => {
    if (userLocationPermission === 'denied') {
      console.log('Permissão de localização negada anteriormente');
      return;
    }
    console.log('📍 Solicitando localização do usuário (recurso opcional)...');

    if (!navigator.geolocation) {
      console.log('ℹ️ Geolocalização não é suportada pelo navegador - continuando sem localização do usuário');
      setUserLocationPermission('unsupported');
      return;
    }

    // Verificar se estamos em um contexto seguro (HTTPS) ou localhost
    const isSecureContext = window.isSecureContext;
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    if (!isSecureContext && !isLocalhost) {
      console.warn('ℹ️ Geolocalização requer contexto seguro (HTTPS) exceto em localhost - continuando sem localização');
      setUserLocationPermission('insecure');
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000, // 10 seconds
      maximumAge: 0 // Force fresh location
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log('✅ Localização obtida:', position.coords);
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        if (!isNaN(lat) && !isNaN(lng)) {
          const userLoc = {
            lat: lat,
            lng: lng
          };
          console.log('📍 Localização do usuário definida:', userLoc);
          setUserLocation(userLoc);
          setUserLocationPermission('granted');
        } else {
          console.error('❌ Coordenadas de localização inválidas');
          setUserLocationPermission('error');
        }
      },
      (error) => {
        console.log('ℹ️ Não foi possível obter localização do usuário (recurso opcional):', error.message);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            console.log('ℹ️ Usuário negou a solicitação de localização - continuando sem este recurso');
            setUserLocationPermission('denied');
            break;
          case error.POSITION_UNAVAILABLE:
            console.log('ℹ️ Localização não disponível - continuando sem este recurso');
            setUserLocationPermission('unavailable');
            break;
          case error.TIMEOUT:
            console.log('ℹ️ Tempo limite excedido para obter localização - continuando sem este recurso');
            setUserLocationPermission('timeout');
            break;
          default:
            console.log('ℹ️ Erro ao obter localização - continuando sem este recurso');
            setUserLocationPermission('error');
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutos
      }
    );
  }, []);

  // Solicitar localização do usuário assim que o componente carregar
  useEffect(() => {
    getUserLocation();
  }, [getUserLocation]);

  // Fetch orders from Supabase
  const fetchOrders = useCallback(async () => {
    if (!driverId) return;

    console.log('💶 Buscando dados do motorista e pedidos...', { driverId });

    try {
      // Primeiro autenticar com a chave anônima para acessar dados protegidos por RLS
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // Se não tem sessão, faz login anônimo
        await supabase.auth.signInAnonymously();
        console.log('🔑 Autenticação anônima realizada');
      }
      // Primeiro, buscar os dados do motorista pelo ID no Authentication
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, display_name')
        .eq('id', driverId)
        .maybeSingle(); // usar maybeSingle ao invés de single para não dar erro se não encontrar

      if (userError) {
        console.error('❌ Erro ao buscar dados do motorista:', userError);
        setError('Erro ao buscar dados do motorista: ' + userError.message);
        setLoading(false);
        return;
      }

      if (!userData) {
        console.error('❌ Motorista não encontrado');
        setError('Motorista não encontrado no sistema');
        setLoading(false);
        return;
      }

      if (!userData.display_name) {
        console.error('❌ Motorista não encontrado ou sem nome de exibição');
        throw new Error('Motorista não encontrado');
      }

      const driverName = userData.display_name;
      console.log('✅ Dados do motorista carregados:', { id: driverId, name: driverName });

      // Depois, buscar os pedidos associados ao motorista
      // Obter data de hoje (início e fim do dia)
      const today = new Date();
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString();
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999).toISOString();
      
      console.log('🔍 Filtrando pedidos de hoje:', { startOfDay, endOfDay });
      
      const { data: ordersData, error: ordersError } = await supabase
        .from('deliveries')
        .select('*')
        .eq('delivery_status', 'in_transit')
        .eq('delivery_person_name', driverName)
        .gte('created_at', startOfDay)
        .lt('created_at', endOfDay);

      if (ordersError) {
        console.error('❌ Erro ao buscar pedidos:', ordersError);
        setError('Erro ao buscar pedidos: ' + ordersError.message);
        setLoading(false);
        return;
      }

      const orders = Array.isArray(ordersData) ? ordersData : [];
      setOrders(orders);

      if (orders.length === 0) {
        console.log('ℹ️ Nenhum pedido encontrado para este motorista');
      }
    } catch (err) {
      console.error('❌ Erro ao buscar pedidos:', err);
      setError('Erro ao carregar pedidos: ' + err.message);
    }
  }, [driverId]);

  // Fetch driver location from Supabase
  const fetchDriverLocation = useCallback(async () => {
    if (!driverId) return;

    console.log('🚗 Buscando localização do motorista...', { driverId });

    try {
      let { data, error } = await supabase
        .from('locations')
        .select('latitude, longitude')
        .eq('user_id', driverId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;
      if (data && data.length > 0) {
        const location = {
          lat: parseFloat(data[0].latitude),
          lng: parseFloat(data[0].longitude)
        };
        console.log('✅ Localização do motorista carregada:', location);
        setDriverLocation(location);
      } else {
        console.log('⚠️ Nenhuma localização encontrada para o motorista');
      }
    } catch (err) {
      console.error('❌ Erro ao buscar localização do motorista:', err);
    }
  }, [driverId]);

  // Calculate directions with waypoints
  const calculateDirections = useCallback((startPoint, waypoints = []) => {
    console.log('🗺️ Iniciando cálculo de direções...', {
      startPoint: !!startPoint,
      waypoints: waypoints.length,
      googleLoaded: !!googleRef.current,
      isMapLoaded: isMapLoaded
    });

    if (!googleRef.current) {
      console.log('⏳ Google Maps ainda não está carregado. Direções serão calculadas quando estiver pronto.');
      return;
    }

    if (!startPoint) {
      console.log('ℹ️ Ponto de partida não disponível, usando localização padrão');
      // Usar uma localização padrão se não tivermos a localização do motorista
      startPoint = center;
    }

    if (waypoints.length === 0) {
      console.log('ℹ️ Nenhum waypoint disponível para calcular direções');
      return;
    }

    const directionsService = new googleRef.current.maps.DirectionsService();

    // Verificar e garantir que todos os waypoints são válidos
    const validWaypoints = waypoints.filter(point => {
      return point && point.lat && point.lng &&
        !isNaN(parseFloat(point.lat)) &&
        !isNaN(parseFloat(point.lng));
    });

    console.log('✅ Waypoints validados:', validWaypoints);

    if (validWaypoints.length === 0) {
      console.error('❌ Nenhum waypoint válido encontrado');
      return;
    }

    // Criar objetos de waypoints para o serviço de direções
    const waypointObjects = validWaypoints.map(point => ({
      location: { lat: parseFloat(point.lat), lng: parseFloat(point.lng) },
      stopover: true
    }));

    // Usar o último waypoint como destino
    const lastWaypoint = waypointObjects.pop();
    const destination = lastWaypoint.location;

    // Garantir que startPoint tem valores numéricos válidos
    const origin = {
      lat: parseFloat(startPoint.lat),
      lng: parseFloat(startPoint.lng)
    };

    if (isNaN(origin.lat) || isNaN(origin.lng)) {
      console.error('❌ Coordenadas de origem inválidas:', startPoint);
      return;
    }

    console.log('🚀 Fazendo requisição de direções...', { origin, destination, waypoints: waypointObjects.length });

    directionsService.route(
      {
        origin: origin,
        destination: destination,
        waypoints: waypointObjects,
        travelMode: googleRef.current.maps.TravelMode.DRIVING,
        optimizeWaypoints: true, // Deixar o Google otimizar a ordem dos waypoints
        avoidHighways: false,
        avoidTolls: false
      },
      (result, status) => {
        console.log('📋 Resposta do serviço de direções:', { status });
        if (status === googleRef.current.maps.DirectionsStatus.OK) {
          console.log('✅ Direções calculadas com sucesso!');
          setDirections(result);
        } else {
          console.warn('⚠️ Não foi possível calcular direções:', status);
          // Tentar novamente com configurações mais simples
          directionsService.route(
            {
              origin: origin,
              destination: destination,
              travelMode: googleRef.current.maps.TravelMode.DRIVING,
            },
            (simpleResult, simpleStatus) => {
              if (simpleStatus === googleRef.current.maps.DirectionsStatus.OK) {
                console.log('✅ Direções simples calculadas com sucesso!');
                setDirections(simpleResult);
              } else {
                console.error('❌ Falha ao calcular direções mesmo no modo simples:', simpleStatus);
              }
            }
          );
        }
      }
    );
  }, [isMapLoaded]);

  // Load data when driver ID is available
  useEffect(() => {
    if (driverId) {
      fetchOrders();
      fetchDriverLocation();
      getUserLocation(); // Request user's location (opcional)

      // Definir um timeout para continuar mesmo se não conseguirmos todos os dados
      const timeout = setTimeout(() => {
        setLoading(false);
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [driverId, fetchOrders, fetchDriverLocation, getUserLocation]);

  // Set up real-time location updates
  useEffect(() => {
    if (!driverId) return;

    console.log('🔄 Configurando atualizações em tempo real para:', driverId);

    const locationSubscription = supabase
      .channel('location-updates')
      .on('postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'locations',
          filter: `user_id=eq.${driverId}`
        },
        (payload) => {
          console.log('📡 Atualização de localização recebida:', payload);
          if (payload.new && payload.new.latitude && payload.new.longitude) {
            const newLocation = {
              lat: parseFloat(payload.new.latitude),
              lng: parseFloat(payload.new.longitude)
            };
            console.log('📍 Nova localização do motorista:', newLocation);
            setDriverLocation(newLocation);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(locationSubscription);
    };
  }, [driverId]);

  // Calculate route when orders and driver location are loaded
  useEffect(() => {
    // Use startLocation da URL, se disponível, senão usar driverLocation
    const startPoint = startLocation || driverLocation;

    if (orders.length > 0 && startPoint) {
      console.log('🚗 Calculando rota...', {
        orders: orders.length,
        startPoint: startLocation ? 'Da URL' : 'Localização do motorista',
        googleLoaded: !!googleRef.current,
        isMapLoaded: isMapLoaded
      });
      try {
        // Create waypoints from orders with validation
        const waypoints = orders.map(order => {
          console.log('📍 Coordenadas do pedido waypoints:', order.coords);
          // Formato do banco: (-48.07124,-23.58511)
          const coordsStr = order.coords?.replace('(', '').replace(')', '').split(',');
          const lng = parseFloat(coordsStr?.[0] || 0);
          const lat = parseFloat(coordsStr?.[1] || 0);
          return {
            lat: lat,
            lng: lng
          };
        }).filter(point => !isNaN(point.lat) && !isNaN(point.lng));

        console.log('📍 Waypoints criados:', waypoints);

        // Só calcular direções se o Google Maps já estiver carregado
        if (googleRef.current && isMapLoaded) {
          // Use startLocation da URL, se disponível, senão usar driverLocation
          const startPoint = startLocation || driverLocation;
          calculateDirections(startPoint, waypoints);
        } else {
          console.log('⏳ Aguardando carregamento do Google Maps para calcular direções...');
        }
      } catch (error) {
        console.error('❌ Erro ao processar waypoints:', error);
      }
    }
  }, [orders, driverLocation, calculateDirections, isMapLoaded]);

  // Função para lidar com o carregamento da API do Google Maps
  const handleMapLoad = useCallback((map) => {
    console.log('🗺️ Google Maps carregado!', { window: !!window.google, maps: !!window.google?.maps });
    googleRef.current = window.google;
    setIsMapLoaded(true);
    console.log('✅ googleRef.current definido:', !!googleRef.current);

    // Recalcular rotas se já tivermos os dados necessários
    if (orders.length > 0) {
      console.log('🔄 Recalculando rotas após carregamento do Google Maps');
      try {
        // Create waypoints from orders with validation
        const waypoints = orders.map(order => {
          // Formato do banco: (-48.07124,-23.58511)
          const coordsStr = order.coords?.replace('(', '').replace(')', '').split(',');
          const lng = parseFloat(coordsStr?.[0] || 0);
          const lat = parseFloat(coordsStr?.[1] || 0);
          return {
            lat: lat,
            lng: lng
          };
        }).filter(point => !isNaN(point.lat) && !isNaN(point.lng));

        console.log('📍 Waypoints criados após carregamento do Maps:', waypoints);
        // Usar a localização do motorista se disponível, caso contrário usar o centro padrão
        const startPoint = driverLocation || center;
        calculateDirections(startPoint, waypoints);
      } catch (error) {
        console.error('❌ Erro ao processar waypoints após carregamento:', error);
      }
    }
  }, [orders, driverLocation, calculateDirections]);

  // Definir loading como false quando tivermos as informações mínimas necessárias
  useEffect(() => {
    if (driverId && orders.length > 0 && driverLocation) {
      setLoading(false);
    }
  }, [driverId, orders, driverLocation]);

  // Prevent zoom on mobile
  useEffect(() => {
    const preventDefault = (e) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    document.addEventListener('gesturestart', preventDefault);
    document.addEventListener('gesturechange', preventDefault);
    document.addEventListener('gestureend', preventDefault);

    // Prevent pull-to-refresh on mobile
    document.body.style.overscrollBehaviorY = 'contain';
    
    // Disable double tap zoom
    let lastTap = 0;
    const handleTouchStart = (e) => {
      const currentTime = new Date().getTime();
      const tapLength = currentTime - lastTap;
      if (tapLength < 300 && tapLength > 0) {
        e.preventDefault();
        e.stopPropagation();
      }
      lastTap = currentTime;
    };
    
    document.addEventListener('touchstart', handleTouchStart, { passive: false });

    return () => {
      document.removeEventListener('gesturestart', preventDefault);
      document.removeEventListener('gesturechange', preventDefault);
      document.removeEventListener('gestureend', preventDefault);
      document.removeEventListener('touchstart', handleTouchStart);
      document.body.style.overscrollBehaviorY = '';
    };
  }, []);

  if (loading && !driverId) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando informações de entrega...</p>
      </div>
    );
  }

  if (error && !driverId) {
    return (
      <div className="error-container">
        <h2>Erro</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="warning-banner">
        <div className="warning-content">
          <span className="warning-icon">ℹ️️</span>
          <p>
            Embora o sistema mostre as paradas do entregador, é ele quem decide a ordem das entregas.
            Fique atento ao WhatsApp - você será notificado quando ele estiver próximo!
          </p>
        </div>
      </div>

      {error && (
        <div className="info-banner">
          <p>{error}</p>
        </div>
      )}

      <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
        loadingElement={<div>Carregando mapa...</div>}
        onLoad={() => {
          console.log('🌎 Script do Google Maps carregado');
          setLoading(false); // Garantir que o app continue mesmo que outros dados não estejam prontos
        }}
        onError={(error) => {
          console.error('❌ Erro ao carregar script do Google Maps:', error);
          // Não definir erro para não bloquear a interface
          setLoading(false);
        }}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={13}
          onLoad={handleMapLoad}
          options={{
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: true,
            styles: [
              {
                featureType: "poi",
                stylers: [{ visibility: "off" }]
              },
              {
                featureType: "poi.business",
                stylers: [{ visibility: "off" }]
              },
              {
                featureType: "poi.government",
                stylers: [{ visibility: "off" }]
              },
              {
                featureType: "poi.medical",
                stylers: [{ visibility: "off" }]
              },
              {
                featureType: "poi.park",
                stylers: [{ visibility: "off" }]
              },
              {
                featureType: "poi.place_of_worship",
                stylers: [{ visibility: "off" }]
              },
              {
                featureType: "poi.school",
                stylers: [{ visibility: "off" }]
              },
              {
                featureType: "poi.sports_complex",
                stylers: [{ visibility: "off" }]
              },
              {
                featureType: "poi.attraction",
                stylers: [{ visibility: "off" }]
              }
            ]
          }}
        >
          {/* {directions && (
            <DirectionsRenderer
              directions={directions}
              options={{
                polylineOptions: {
                  strokeColor: '#4285F4',
                  strokeWeight: 4
                },
                suppressInfoWindows: true,
                hideRouteList: true,
                suppressMarkers: true // Esconder marcadores padrão para poder usar nossos próprios
              }}
            />
          )} */}

          {/* Marcador para o ponto de partida (Restaurante/Loja) */}
          {directions && directions.routes && directions.routes[0] &&
            directions.routes[0].legs && directions.routes[0].legs[0] &&
            directions.routes[0].legs[0].start_location && googleRef.current && (
              <Marker
                position={directions.routes[0].legs[0].start_location}
                icon={{
                  url: 'https://storage.googleapis.com/flutterflow-io-6f20.appspot.com/projects/safe-tag-l5p8b4/assets/cy54ez02t8g1/shop-marker-icon.png',
                  scaledSize: new googleRef.current.maps.Size(31, 46),
                  anchor: new googleRef.current.maps.Point(31, 46)
                }}
                title="Restaurante/Loja (Ponto de Partida)"
              />
            )}

          {/* Marcadores para os pontos de parada (waypoints) */}
          {directions && directions.routes && directions.routes[0] &&
            directions.routes[0].legs && googleRef.current &&
            directions.routes[0].legs.map((leg, i) => (
              <React.Fragment key={i}>
                {/* Não repete o marcador de início para o primeiro waypoint */}
                {i > 0 && leg.start_location && (
                  <Marker
                    position={leg.start_location}
                    label={{ text: String.fromCharCode(66 + i - 1), color: "white" }}
                    icon={{
                      path: googleRef.current.maps.SymbolPath.CIRCLE,
                      fillColor: "#FF5252",
                      fillOpacity: 1,
                      strokeWeight: 2,
                      strokeColor: "#FFFFFF",
                      scale: 10
                    }}
                    title={`Parada ${String.fromCharCode(66 + i - 1)}`}
                  />
                )}

                {/* Destino final */}
                {i === directions.routes[0].legs.length - 1 && leg.end_location && (
                  <Marker
                    position={leg.end_location}
                    label={{
                      text: String.fromCharCode(65 + directions.routes[0].legs.length),
                      color: "white"
                    }}
                    icon={{
                      path: googleRef.current.maps.SymbolPath.CIRCLE,
                      fillColor: "#FF5252",
                      fillOpacity: 1,
                      strokeWeight: 2,
                      strokeColor: "#FFFFFF",
                      scale: 10
                    }}
                    title={`Destino Final`}
                  />
                )}
              </React.Fragment>
            ))}

          {driverLocation && googleRef.current && (
            <Marker
              position={driverLocation}
              icon={{
                url: 'https://storage.googleapis.com/flutterflow-io-6f20.appspot.com/projects/safe-tag-l5p8b4/assets/pwjb1uwkjfjp/in-transit-marker-icon.png',
                scaledSize: googleRef.current.maps.Size ? new googleRef.current.maps.Size(31, 46) : null,
                anchor: googleRef.current.maps.Point ? new googleRef.current.maps.Point(31, 46) : null
              }}
              title="Localização do Entregador"
              zIndex={1000} // Coloca o motorista acima dos outros marcadores
            />
          )}

          {userLocation && userLocationPermission === 'granted' && googleRef.current && (
            <Marker
              position={userLocation}
              icon={{
                url: 'https://storage.googleapis.com/flutterflow-io-6f20.appspot.com/projects/safe-tag-l5p8b4/assets/66vtmwuf9bzn/home-marker-icon.png',
                scaledSize: googleRef.current.maps.Size ? new googleRef.current.maps.Size(31, 46) : null,
                anchor: googleRef.current.maps.Point ? new googleRef.current.maps.Point(31, 46) : null
              }}
              title="Sua localização"
            />
          )}
        </GoogleMap>
      </LoadScript>

      <div className="info-panel">
        <h3>Status da Entrega</h3>
        <div className="delivery-info">
          <p><strong>Pedidos em rota:</strong> {orders.length}</p>
        </div>
      </div>
    </div>
  );
}

export default App;
